<template>
  <div class="faq-preset-section mt-7">
    <div class="text-subtitle-1 font-weight-bold mb-1">預設問答</div>
    <div class="text-body-2 grey--text text--darken-1 mb-4">
      <template v-if="canEdit">
        拖拉左側把手可調整問題順序；展開後可勾選／新增回覆，並可拖拉回覆排序或刪除任一選項。回覆字數上限 225 字
      </template>
      <template v-else>
        以下為目前顯示於網站的問答設定，點擊可展開查看回覆。
      </template>
    </div>

    <draggable
      :value="items"
      @input="updateItems"
      :disabled="!canEdit"
      handle=".question-drag"
      animation="200"
    >
      <transition-group type="transition" name="flip-list">
        <div
          v-for="(preset, pIdx) in items"
          :key="preset.id || `preset-${pIdx}`"
          class="faq-card mb-3 rounded-lg"
        >
          <!-- 問題標題行 -->
          <div
            class="faq-question-row d-flex align-center cursor-pointer"
            :class="canEdit ? 'faq-question-row--edit px-4' : 'pa-3'"
            @click="toggleExpand(pIdx)"
          >
            <v-icon
              v-if="canEdit"
              class="question-drag mr-2"
              color="grey"
              @click.stop
            >mdi-drag</v-icon>
            <span class="flex-grow-1 text-body-2" :class="canEdit ? 'font-weight-bold' : 'font-weight-medium'">{{ preset.question }}</span>
            <v-icon>{{ expanded[pIdx] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>

          <!-- 展開內容 -->
          <div v-if="expanded[pIdx]" class="faq-options-area" :class="canEdit ? 'faq-options-area--edit pa-4 pl-7' : 'px-3 pb-3'">
            <!-- View 模式 -->
            <template v-if="!canEdit">
              <ul class="pl-4">
                <li
                  v-for="(opt, oIdx) in selectedOptions(preset)"
                  :key="oIdx"
                  class="text-body-2 mb-1"
                >{{ opt.option_text }}</li>
              </ul>
              <div v-if="selectedOptions(preset).length === 0" class="text-body-2 grey--text">
                尚無已勾選的回覆
              </div>
            </template>

            <!-- Edit 模式 -->
            <template v-else>
              <draggable
                :value="preset.options"
                @input="val => updateOptions(pIdx, val)"
                handle=".option-drag"
                animation="200"
              >
                <transition-group type="transition" name="flip-list">
                  <div
                    v-for="(opt, oIdx) in preset.options"
                    :key="opt.id || `opt-${oIdx}`"
                    class="d-flex align-center mb-2"
                  >
                    <v-icon class="option-drag mr-2" color="grey">mdi-drag</v-icon>
                    <v-checkbox
                      :input-value="opt.is_selected"
                      @change="val => updateOptionField(pIdx, oIdx, 'is_selected', val ? 1 : 0)"
                      hide-details
                      dense
                      class="mt-0 pt-0 mr-2 shrink"
                    />
                    <v-text-field
                      :value="opt.option_text"
                      @input="val => updateOptionField(pIdx, oIdx, 'option_text', val)"
                      placeholder="輸入回覆內容..."
                      dense
                      hide-details
                      outlined
                      :maxlength="225"
                      counter="225"
                      class="flex-grow-1"
                      :error="!opt.option_text || !opt.option_text.trim()"
                    />
                    <v-btn icon small color="grey" class="ml-1" @click="removeOption(pIdx, oIdx)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </transition-group>
              </draggable>
              <v-btn
                v-if="preset.options.length < 10"
                text
                small
                color="primary"
                class="mt-1"
                @click="addOption(pIdx)"
              >
                <v-icon left small>mdi-plus</v-icon>
                新增自訂回覆
              </v-btn>
            </template>
          </div>
        </div>
      </transition-group>
    </draggable>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'FaqPresetSection',
  components: { draggable },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expanded: {}
    }
  },
  methods: {
    toggleExpand(idx) {
      this.$set(this.expanded, idx, !this.expanded[idx])
    },
    selectedOptions(preset) {
      return (preset.options || []).filter(o => o.is_selected === 1)
    },
    updateItems(val) {
      this.$emit('update:items', val)
    },
    updateOptions(pIdx, newOptions) {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems[pIdx].options = newOptions
      this.$emit('update:items', newItems)
    },
    updateOptionField(pIdx, oIdx, field, val) {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems[pIdx].options[oIdx][field] = val
      this.$emit('update:items', newItems)
    },
    addOption(pIdx) {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems[pIdx].options.push({
        option_text: '',
        is_selected: 1
      })
      this.$emit('update:items', newItems)
    },
    removeOption(pIdx, oIdx) {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems[pIdx].options.splice(oIdx, 1)
      this.$emit('update:items', newItems)
    }
  }
}
</script>

<style scoped lang="scss">
.faq-card {
  border: 1px solid #E5E5E5;
  background: #F9F9F9;
  border-radius: 6px;
  overflow: hidden;
}

.faq-question-row--edit {
  background: #F0F0F0;
  border-bottom: 1px solid #E5E5E5;
  min-height: 44px;
}

.faq-options-area--edit {
  background: #F9F9F9;
}

.cursor-pointer {
  cursor: pointer;
}

.question-drag,
.option-drag {
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
}

.flip-list-move {
  transition: transform 0.3s;
}
</style>
